import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Save, X } from "lucide-react";

const emptyChallan = () => ({
  bsrCode: "",
  challanSerialNumber: "",
  cin: "",
  dateOfDeposit: "",
  totalAmountDeposited: "",
  tdsAmount: "",
  surchargeAmount: "0",
  cessAmount: "0",
  interestAmount: "0",
  othersAmount: "0",
  fee234EAmount: "0",
  isNilChallan: false,
  bankName: "",
  paymentMode: "E-Payment",
  remarks: "",
});

export { emptyChallan };

/**
 * Add / Edit ITNS 281 challan — portal modal (matches DeductorMaster / Link patterns).
 */
export default function AddChallanModal({
  open,
  onClose,
  darkMode = false,
  initial = null,
  financialYear,
  quarter,
  saving = false,
  onSave,
}) {
  const [draft, setDraft] = useState(emptyChallan());

  useEffect(() => {
    if (!open) return;
    setDraft(initial && Object.keys(initial).length ? { ...emptyChallan(), ...initial } : emptyChallan());
  }, [open, initial]);

  if (!open || typeof document === "undefined") return null;

  const inputCls = `mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
    darkMode ? "border-[#555] bg-[#2a2a2a] text-white" : "border-gray-300 bg-white"
  }`;
  const isEdit = !!draft.id;

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    onSave?.(draft);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/50 p-4"
      onClick={() => !saving && onClose(false)}
    >
      <div
        className={`flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl shadow-xl ${
          darkMode ? "bg-[#1e1e1e] text-white" : "bg-white"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? "Edit challan" : "Add challan"}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-base font-bold">
              {isEdit ? "Edit challan (ITNS 281)" : "Add challan (ITNS 281)"}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              {financialYear} · {quarter} · Form 24Q / Section 192
            </p>
          </div>
          <button
            type="button"
            disabled={saving}
            onClick={() => onClose(false)}
            className="rounded-lg p-1 hover:bg-gray-100 dark:hover:bg-[#333]"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="min-h-0 flex-1 overflow-auto px-5 py-4">
          <label className="mb-4 flex items-center gap-2 text-xs font-medium">
            <input
              type="checkbox"
              checked={!!draft.isNilChallan}
              onChange={(e) => setDraft((p) => ({ ...p, isNilChallan: e.target.checked }))}
            />
            Nil challan (book adjustment)
          </label>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              ["bsrCode", "BSR code *"],
              ["challanSerialNumber", "Challan serial no. *"],
              ["cin", "CIN"],
              ["dateOfDeposit", "Deposit date *", "date"],
              ["tdsAmount", "Income tax / TDS *"],
              ["surchargeAmount", "Surcharge"],
              ["cessAmount", "Cess"],
              ["interestAmount", "Interest"],
              ["fee234EAmount", "Fee u/s 234E"],
              ["othersAmount", "Others"],
              ["totalAmountDeposited", "Total paid (optional)"],
              ["bankName", "Bank name"],
            ].map(([key, label, type]) => (
              <label key={key} className="block text-xs font-medium">
                {label}
                <input
                  type={type || "text"}
                  value={draft[key] ?? ""}
                  onChange={(e) => setDraft((p) => ({ ...p, [key]: e.target.value }))}
                  className={inputCls}
                  disabled={draft.isNilChallan && ["bsrCode", "challanSerialNumber"].includes(key)}
                  required={
                    !draft.isNilChallan &&
                    ["bsrCode", "challanSerialNumber", "dateOfDeposit", "tdsAmount"].includes(key)
                  }
                />
              </label>
            ))}
            <label className="block text-xs font-medium">
              Payment mode
              <select
                value={draft.paymentMode || "E-Payment"}
                onChange={(e) => setDraft((p) => ({ ...p, paymentMode: e.target.value }))}
                className={inputCls}
              >
                <option>E-Payment</option>
                <option>Cheque / DD</option>
                <option>Transfer</option>
                <option>Book adjustment</option>
              </select>
            </label>
            <label className="block text-xs font-medium sm:col-span-2 lg:col-span-3">
              Remarks
              <input
                type="text"
                value={draft.remarks || ""}
                onChange={(e) => setDraft((p) => ({ ...p, remarks: e.target.value }))}
                className={inputCls}
                placeholder="Optional"
              />
            </label>
          </div>
        </form>

        <div
          className={`flex flex-wrap items-center justify-end gap-2 border-t px-5 py-3 ${
            darkMode ? "border-[#333] bg-[#242424]" : "border-gray-100 bg-gray-50"
          }`}
        >
          <button
            type="button"
            disabled={saving}
            onClick={() => onClose(false)}
            className={`rounded-lg border px-4 py-2 text-sm ${
              darkMode ? "border-[#555]" : "border-gray-300"
            }`}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={handleSubmit}
            className="inline-flex items-center gap-2 rounded-lg bg-redhalo-red px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {isEdit ? "Update challan" : "Save challan"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
